import ExcelJS from 'exceljs';
/**
 * 엑셀 파일 다운로드 헬퍼 함수
 * @param buffer - ExcelJS 버퍼
 * @param fileName - 다운로드할 파일명
 */
function downloadExcel(buffer, fileName) {
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}
function collectExportColumns(columnDefs, parentHeaders = []) {
    return columnDefs.flatMap((col) => {
        if ('children' in col && col.children && col.children.length > 0) {
            const header = col.headerName ? String(col.headerName) : '';
            const nextHeaders = header ? [...parentHeaders, header] : parentHeaders;
            return collectExportColumns(col.children, nextHeaders);
        }
        const headerName = col.headerName ? String(col.headerName) : '';
        return [{ colDef: col, headerPath: [...parentHeaders, headerName] }];
    });
}
function toNumericValue(value, decimals) {
    if (value == null || value === '')
        return value;
    if (typeof value === 'number') {
        return decimals !== undefined ? Number(value.toFixed(decimals)) : value;
    }
    if (typeof value !== 'string')
        return value;
    const trimmed = value.trim();
    if (trimmed === '-')
        return trimmed;
    const normalized = trimmed.replace(/,/g, '').replace(/^\+/, '');
    if (normalized === '')
        return value;
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed))
        return value;
    return decimals !== undefined ? Number(parsed.toFixed(decimals)) : parsed;
}
function getNumericFieldConfig(field, numericFields) {
    if (!numericFields)
        return null;
    const config = numericFields.find((item) => {
        if (typeof item === 'string')
            return item === field;
        return item.field === field;
    });
    if (!config)
        return null;
    if (typeof config === 'string')
        return { field };
    return config;
}
function formatCellValue(value, colDef, rowData, options) {
    const resolved = options.resolveCellValue?.({ value, colDef, rowData });
    if (resolved !== undefined) {
        return resolved;
    }
    const isEmptyOrZero = value === 0 || value === '0' || value == null || value === '';
    if (colDef.field && options.zeroToDashFields?.includes(colDef.field) && isEmptyOrZero) {
        return '-';
    }
    if (colDef.valueFormatter) {
        try {
            if (typeof colDef.valueFormatter === 'function') {
                const params = {
                    value,
                    data: rowData,
                    node: null,
                    colDef,
                    column: null,
                    api: null,
                    context: null,
                };
                const formatted = colDef.valueFormatter(params);
                const resolvedValue = formatted ?? (value == null ? '-' : value);
                if (colDef.field) {
                    const numericConfig = getNumericFieldConfig(colDef.field, options.numericFields);
                    if (numericConfig) {
                        return toNumericValue(resolvedValue, numericConfig.decimals);
                    }
                }
                return resolvedValue;
            }
            const resolvedValue = colDef.valueFormatter;
            if (colDef.field) {
                const numericConfig = getNumericFieldConfig(colDef.field, options.numericFields);
                if (numericConfig) {
                    return toNumericValue(resolvedValue, numericConfig.decimals);
                }
            }
            return resolvedValue;
        }
        catch (error) {
            console.warn('valueFormatter 실행 중 오류:', error);
            const resolvedValue = value == null ? '-' : value;
            if (colDef.field) {
                const numericConfig = getNumericFieldConfig(colDef.field, options.numericFields);
                if (numericConfig) {
                    return toNumericValue(resolvedValue, numericConfig.decimals);
                }
            }
            return resolvedValue;
        }
    }
    if (colDef.cellRenderer) {
        const resolvedValue = value ?? '';
        if (colDef.field) {
            const numericConfig = getNumericFieldConfig(colDef.field, options.numericFields);
            if (numericConfig) {
                return toNumericValue(resolvedValue, numericConfig.decimals);
            }
        }
        return resolvedValue;
    }
    const resolvedValue = value == null ? '-' : value;
    if (colDef.field) {
        const numericConfig = getNumericFieldConfig(colDef.field, options.numericFields);
        if (numericConfig) {
            return toNumericValue(resolvedValue, numericConfig.decimals);
        }
    }
    return resolvedValue;
}
/**
 * 데이터 그리드 컬럼/데이터를 엑셀로 내보내기
 * @param data - 내보낼 상세 데이터
 * @param columnDefs - 컬럼 정의 배열
 * @param options - 파일명/시트명/필드 옵션
 */
export async function exportDataGridToExcel(data, columnDefs, options = {}) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(options.sheetName ?? '상세내역');
    const exportColumns = collectExportColumns(columnDefs).filter((col) => col.colDef.field &&
        col.colDef.headerName &&
        !options.excludeFields?.includes(col.colDef.field));
    const maxHeaderDepth = Math.max(1, ...exportColumns.map((col) => col.headerPath.length));
    const getColumnWidth = (col) => {
        if (col.width)
            return col.width / 7;
        if (col.minWidth)
            return col.minWidth / 7;
        return 15;
    };
    worksheet.columns = exportColumns.map(({ colDef, headerPath }, index) => {
        return {
            header: headerPath[headerPath.length - 1] || '',
            key: colDef.field || `col_${index}`,
            width: getColumnWidth(colDef),
        };
    });
    const paddedHeaders = exportColumns.map((col) => {
        const padding = Array.from({ length: maxHeaderDepth - col.headerPath.length }, () => '');
        return [...col.headerPath, ...padding];
    });
    for (let rowIndex = 1; rowIndex <= maxHeaderDepth; rowIndex += 1) {
        const headerRow = worksheet.getRow(rowIndex);
        paddedHeaders.forEach((headerPath, colIndex) => {
            const cell = headerRow.getCell(colIndex + 1);
            cell.value = headerPath[rowIndex - 1] || '';
        });
        headerRow.font = { bold: true, size: 11 };
        headerRow.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE0E0E0' },
        };
        headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
        headerRow.height = 20;
    }
    if (maxHeaderDepth > 1) {
        const groupLabels = exportColumns.map((col) => col.headerPath.length > 1 ? col.headerPath[0] : '');
        let groupStartIndex = 0;
        let currentGroup = groupLabels[0] || '';
        for (let index = 0; index <= groupLabels.length; index += 1) {
            const nextGroup = groupLabels[index] || '';
            const isGroupEnd = index === groupLabels.length || nextGroup !== currentGroup;
            if (isGroupEnd && currentGroup && index > groupStartIndex) {
                const start = groupStartIndex + 1;
                const end = index;
                worksheet.mergeCells(1, start, 1, end);
            }
            if (isGroupEnd) {
                groupStartIndex = index;
                currentGroup = nextGroup;
            }
        }
        paddedHeaders.forEach((headerPath, index) => {
            const hasGroupHeader = headerPath.length > 1 && headerPath[0] && headerPath[1];
            if (!hasGroupHeader && headerPath[0]) {
                worksheet.mergeCells(1, index + 1, maxHeaderDepth, index + 1);
            }
        });
    }
    exportColumns.forEach(({ colDef }, index) => {
        const cellStyle = typeof colDef.cellStyle === 'function' ? colDef.cellStyle({}) : colDef.cellStyle;
        const column = worksheet.getColumn(index + 1);
        column.alignment =
            cellStyle?.textAlign === 'right'
                ? { horizontal: 'right', vertical: 'middle' }
                : cellStyle?.textAlign === 'center'
                    ? { horizontal: 'center', vertical: 'middle' }
                    : { vertical: 'middle' };
    });
    data.forEach((rowData) => {
        const row = exportColumns.reduce((acc, { colDef }, index) => {
            const field = colDef.field || `col_${index}`;
            const value = rowData[colDef.field];
            acc[field] = formatCellValue(value, colDef, rowData, options);
            return acc;
        }, {});
        worksheet.addRow(row);
    });
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const finalFileName = `${options.fileName ?? '내역'}_${dateStr}`;
    const buffer = await workbook.xlsx.writeBuffer();
    downloadExcel(buffer, finalFileName);
}
//# sourceMappingURL=exportDataGridToExcel.js.map